import { PageHeader, PasswordRequirementsHint, PageSkeleton } from '@euroasiann/shared/ui';
import { isPasswordStrong, PASSWORD_POLICY_MESSAGE } from '@euroasiann/shared';
import { authenticatedFetch } from '../../lib/api';
import { useToast } from '../../components/shared/Toast';
import { useCurrency } from '../../context/CurrencyContext';
import { useDateFormat } from '../../context/DateFormatContext';
import { ALL_CURRENCIES, loadStoredCurrency } from '../../lib/currency';
import { DISPLAY_SETTINGS_KEY, DATE_FORMAT_OPTIONS } from '../../lib/date-format';
import { useTheme, type ThemePreference } from '../../context/ThemeContext';
import { useFont } from '../../context/FontContext';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import {
  MdNotifications as MdNotificationsIcon,
  MdSecurity as MdSecurityIcon,
  MdPalette as MdPaletteIcon,
  MdVisibility as MdVisibilityIcon,
  MdVisibilityOff as MdVisibilityOffIcon,
  MdGavel as MdGavelIcon,
  MdInfo as MdInfoIcon,
  MdKeyboardArrowDown as MdKeyboardArrowDownIcon,
} from 'react-icons/md';
import { useState, useEffect, useMemo, type ComponentType } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';

type IconProps = { className?: string };
const MdNotifications = MdNotificationsIcon as ComponentType<IconProps>;
const MdSecurity = MdSecurityIcon as ComponentType<IconProps>;
const MdPalette = MdPaletteIcon as ComponentType<IconProps>;
const MdVisibility = MdVisibilityIcon as ComponentType<IconProps>;
const MdVisibilityOff = MdVisibilityOffIcon as ComponentType<IconProps>;
const MdGavel = MdGavelIcon as ComponentType<IconProps>;
const MdInfo = MdInfoIcon as ComponentType<IconProps>;
const MdKeyboardArrowDown = MdKeyboardArrowDownIcon as ComponentType<IconProps>;

interface NotificationSettings {
  emailNotifications: boolean;
  smsNotifications: boolean;
  rfqAlerts: boolean;
  orderAlerts: boolean;
  paymentAlerts: boolean;
  shipmentAlerts: boolean;
  weeklyReports: boolean;
  monthlyReports: boolean;
}

interface DisplaySettings {
  theme: 'light' | 'dark' | 'auto';
  language: string;
  dateFormat: string;
  timeFormat: string;
  currency: string;
  itemsPerPage: number;
  shipmentMapEnabled: boolean;
}

function getDefaultDisplaySettings(theme: ThemePreference | string): DisplaySettings {
  return {
    theme: (localStorage.getItem('theme') as ThemePreference) || (theme as ThemePreference) || 'auto',
    language: 'en',
    dateFormat: 'MM/DD/YYYY',
    timeFormat: '12h',
    currency: loadStoredCurrency(),
    itemsPerPage: 10,
    shipmentMapEnabled: localStorage.getItem('shipmentMapEnabled') !== 'false',
  };
}

function loadDisplaySettings(theme: ThemePreference | string): DisplaySettings {
  const defaults = getDefaultDisplaySettings(theme);
  try {
    const stored = localStorage.getItem(DISPLAY_SETTINGS_KEY);
    if (stored) {
      return { ...defaults, ...(JSON.parse(stored) as DisplaySettings) };
    }
  } catch {
    // ignore invalid stored settings
  }
  if (theme === 'light' || theme === 'dark' || theme === 'auto') {
    return { ...defaults, theme };
  }
  return defaults;
}

interface SecuritySettings {
  twoFactorEnabled: boolean;
  sessionTimeout: number;
  passwordExpiry: number;
}

export function SettingsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { showToast } = useToast();
  const { theme, setTheme } = useTheme();
  const { currency: portalCurrency, setCurrency: setPortalCurrency } = useCurrency();
  const { dateFormat: portalDateFormat, timeFormat: portalTimeFormat, setDateTimeFormats } =
    useDateFormat();
  const { language: portalLanguage, setLanguage: setPortalLanguage, t } = useLanguage();
  const [activeTab, setActiveTab] = useState<
    'notifications' | 'display' | 'security' | 'procurement'
  >('notifications');

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'banking') {
      navigate('/profile?tab=banking', { replace: true });
      return;
    }
    if (tab === 'notifications' || tab === 'display' || tab === 'security' || tab === 'procurement') {
      setActiveTab(tab);
    }
  }, [searchParams, navigate]);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [notifications, setNotifications] = useState<NotificationSettings>({
    emailNotifications: true,
    smsNotifications: false,
    rfqAlerts: true,
    orderAlerts: true,
    paymentAlerts: true,
    shipmentAlerts: true,
    weeklyReports: false,
    monthlyReports: true,
  });

  const [display, setDisplay] = useState<DisplaySettings>(() => ({
    ...loadDisplaySettings(theme),
    language: portalLanguage,
  }));
  const [savedDisplay, setSavedDisplay] = useState<DisplaySettings>(() => loadDisplaySettings(theme));

  useEffect(() => {
    const loaded = loadDisplaySettings(theme);
    setDisplay((prev) => ({
      ...loaded,
      language: prev.language || portalLanguage,
      dateFormat: portalDateFormat,
      timeFormat: portalTimeFormat,
    }));
    setSavedDisplay({
      ...loaded,
      dateFormat: portalDateFormat,
      timeFormat: portalTimeFormat,
    });
  }, [portalDateFormat, portalTimeFormat]);

  const hasDisplayChanges = useMemo(
    () => JSON.stringify(display) !== JSON.stringify(savedDisplay),
    [display, savedDisplay]
  );

  const [security, setSecurity] = useState<SecuritySettings>({
    twoFactorEnabled: false,
    sessionTimeout: 30,
    passwordExpiry: 90,
  });
  const [savedSecurity, setSavedSecurity] = useState<SecuritySettings>({
    twoFactorEnabled: false,
    sessionTimeout: 30,
    passwordExpiry: 90,
  });
  const [securitySaving, setSecuritySaving] = useState(false);
  const hasSecurityChanges = useMemo(
    () => JSON.stringify(security) !== JSON.stringify(savedSecurity),
    [security, savedSecurity]
  );
  const [passwordSectionOpen, setPasswordSectionOpen] = useState(false);

  // General Settings state
  const { fontSize, setFontSize } = useFont();
  const [desktopNotifications, setDesktopNotifications] = useState({
    newPurchaseOrder: true,
    newRFQ: true,
    withdrawnOrder: true,
    rfqCancelled: true,
    newChatMessage: true,
    reminderReceived: true,
    freightForwardingQuoteRequestSend: true,
    freightForwardingAssignForwarder: true,
  });
  const [defaultCurrency, setDefaultCurrency] = useState(false);

  const [procurement, setProcurement] = useState({
    negotiationLoopEnabled: localStorage.getItem('negotiationLoopEnabled') !== 'false',
  });

  const handleProcurementChange = (key: string, value: any) => {
    setProcurement((prev) => {
      const newState = { ...prev, [key]: value };
      if (key === 'negotiationLoopEnabled') {
        localStorage.setItem('negotiationLoopEnabled', String(value));
      }
      return newState;
    });
  };

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const changePasswordMutation = useMutation({
    mutationFn: async (data: { currentPassword: string; newPassword: string }) => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);
      try {
        const response = await authenticatedFetch('/api/v1/auth/change-password', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
          signal: controller.signal,
        });
        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          throw new Error(error.error || 'Failed to change password');
        }
        return response.json();
      } catch (error: any) {
        if (error.name === 'AbortError') throw new Error('Request timeout. Please try again.');
        throw error;
      } finally {
        clearTimeout(timeoutId);
      }
    },
    onSuccess: () => {
      showToast('Password changed successfully', 'success');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setShowPassword(false);
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to change password', 'error');
    },
  });

  const handleChangePassword = () => {
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      showToast('Please fill in all password fields', 'error');
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      showToast('New passwords do not match', 'error');
      return;
    }
    if (!isPasswordStrong(passwordForm.newPassword)) {
      showToast(PASSWORD_POLICY_MESSAGE, 'error');
      return;
    }
    changePasswordMutation.mutate({
      currentPassword: passwordForm.currentPassword,
      newPassword: passwordForm.newPassword,
    });
  };

  const patchPreferences = async (payload: Record<string, unknown>) => {
    const response = await authenticatedFetch('/api/v1/auth/preferences', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const json = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(json.error || 'Failed to save settings');
    return json.data;
  };

  const handleNotificationChange = async (key: keyof NotificationSettings) => {
    const previous = notifications;
    const next = { ...previous, [key]: !previous[key] };
    setNotifications(next);
    try {
      await patchPreferences({ notifications: next });
    } catch (error: any) {
      setNotifications(previous);
      showToast(error.message || 'Failed to save notification settings', 'error');
    }
  };

  const handleDesktopNotificationChange = async (key: string) => {
    const previous = desktopNotifications;
    const next = { ...previous, [key]: !(previous as Record<string, boolean>)[key] };
    setDesktopNotifications(next);
    try {
      await patchPreferences({ desktopNotifications: next });
    } catch (error: any) {
      setDesktopNotifications(previous);
      showToast(error.message || 'Failed to save notification settings', 'error');
    }
  };

  const handleDefaultCurrencyChange = async (checked: boolean) => {
    const previous = defaultCurrency;
    setDefaultCurrency(checked);
    try {
      await patchPreferences({ defaultCurrency: checked });
    } catch (error: any) {
      setDefaultCurrency(previous);
      showToast(error.message || 'Failed to save notification settings', 'error');
    }
  };

  const handleDisplayChange = (key: keyof DisplaySettings, value: any) => {
    setDisplay((prev) => ({ ...prev, [key]: value }));
  };

  const handleApplyDisplaySettings = () => {
    if (!hasDisplayChanges) return;
    localStorage.setItem(DISPLAY_SETTINGS_KEY, JSON.stringify(display));
    localStorage.setItem('shipmentMapEnabled', String(display.shipmentMapEnabled));
    setTheme(display.theme);
    setPortalCurrency(display.currency as typeof portalCurrency);
    setPortalLanguage(display.language);
    setDateTimeFormats(display.dateFormat, display.timeFormat);
    void authenticatedFetch('/api/v1/auth/preferences', {
      method: 'PUT',
      body: JSON.stringify({
        dateFormat: display.dateFormat,
        timeFormat: display.timeFormat,
        language: display.language,
      }),
    }).catch(() => {
      /* best-effort server sync */
    });
    setSavedDisplay(display);
    showToast(t('Display preferences applied'), 'success');
    window.setTimeout(() => window.location.reload(), 350);
  };

  const handleSecurityChange = (key: keyof SecuritySettings, value: any) => {
    setSecurity((prev) => ({ ...prev, [key]: value }));
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let current: SecuritySettings = {
          twoFactorEnabled: false,
          sessionTimeout: 30,
          passwordExpiry: 90,
        };
        const stored = localStorage.getItem('adminSecuritySettings');
        if (stored) {
          const parsed = JSON.parse(stored) as SecuritySettings;
          current = {
            twoFactorEnabled: Boolean(parsed.twoFactorEnabled),
            sessionTimeout: Number(parsed.sessionTimeout) || current.sessionTimeout,
            passwordExpiry: Number(parsed.passwordExpiry) || current.passwordExpiry,
          };
          if (!cancelled) {
            setSecurity(current);
            setSavedSecurity(current);
          }
        }
        const response = await authenticatedFetch('/api/v1/auth/preferences');
        if (!response.ok) return;
        const json = await response.json();
        const prefs = (json.data || {}) as Record<string, any>;
        if (cancelled) return;
        current = {
          ...current,
          sessionTimeout: Number(prefs.sessionTimeout) || current.sessionTimeout,
          passwordExpiry: Number(prefs.passwordExpiry) || current.passwordExpiry,
          twoFactorEnabled: Boolean(prefs.twoFactorEnabled),
        };
        setSecurity(current);
        setSavedSecurity(current);
        if (prefs.notifications && typeof prefs.notifications === 'object') {
          setNotifications((prev) => ({ ...prev, ...prefs.notifications }));
        }
        if (prefs.desktopNotifications && typeof prefs.desktopNotifications === 'object') {
          setDesktopNotifications((prev) => ({ ...prev, ...prefs.desktopNotifications }));
        }
        if (typeof prefs.defaultCurrency === 'boolean') {
          setDefaultCurrency(prefs.defaultCurrency);
        }
      } catch {
        // keep defaults / local copy
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSaveSecuritySettings = async () => {
    if (!hasSecurityChanges || securitySaving) return;
    const sessionTimeout = Number(security.sessionTimeout);
    const passwordExpiry = Number(security.passwordExpiry);
    if (!Number.isFinite(sessionTimeout) || sessionTimeout < 5 || sessionTimeout > 480) {
      showToast('Session timeout must be between 5 and 480 minutes', 'error');
      return;
    }
    if (!Number.isFinite(passwordExpiry) || passwordExpiry < 30 || passwordExpiry > 365) {
      showToast('Password expiry must be between 30 and 365 days', 'error');
      return;
    }
    setSecuritySaving(true);
    try {
      const payload = {
        sessionTimeout,
        passwordExpiry,
        twoFactorEnabled: security.twoFactorEnabled,
      };
      const response = await authenticatedFetch('/api/v1/auth/preferences', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(json.error || 'Failed to save security settings');
      const saved = (json.data || payload) as SecuritySettings;
      const next = {
        sessionTimeout: Number(saved.sessionTimeout) || sessionTimeout,
        passwordExpiry: Number(saved.passwordExpiry) || passwordExpiry,
        twoFactorEnabled: Boolean(saved.twoFactorEnabled),
      };
      setSecurity(next);
      setSavedSecurity(next);
      localStorage.setItem('adminSecuritySettings', JSON.stringify(next));
      showToast('Security settings saved', 'success');
    } catch (error: any) {
      showToast(error.message || 'Failed to save security settings', 'error');
    } finally {
      setSecuritySaving(false);
    }
  };

  const tabs = [
    { id: 'notifications' as const, label: t('Notifications'), icon: MdNotifications },
    { id: 'display' as const, label: t('Display'), icon: MdPalette },
    { id: 'security' as const, label: t('Security'), icon: MdSecurity },
    { id: 'procurement' as const, label: t('Procurement'), icon: MdGavel },
  ];

  return (
    <div className="w-full min-h-screen bg-[hsl(var(--background))] p-4 lg:p-8 font-sans text-[hsl(var(--foreground))]">
      <PageHeader
        title={t('Settings')}
        subtitle={t('Manage your account settings, preferences, and security options')}
        subtitleVariant="description"
        subtitleAccent={false}
        breadcrumb={[
          { label: t('Admin') },
          { label: t('Settings'), current: true },
        ]}
      />

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        <div className="lg:col-span-1">
          <div className="bg-white rounded-2xl border border-[#0088cc]/25/30 p-2 shadow-sm relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-[#0088cc]/10 rounded-bl-full pointer-events-none" />
            <div className="relative z-10 space-y-1">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => {
                      setActiveTab(tab.id);
                      setSearchParams({ tab: tab.id });
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${activeTab === tab.id
                      ? 'bg-[#0088cc]/15 text-[hsl(var(--foreground))] border border-[#0088cc]/20 shadow-sm font-bold'
                      : 'text-[hsl(var(--foreground))]/60 hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--background))] font-semibold border border-transparent'
                      }`}
                  >
                    <Icon className={`w-5 h-5 ${activeTab === tab.id ? 'text-[hsl(var(--foreground))]' : 'text-[#0088cc]/65'}`} />
                    <span className="text-sm">{tab.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="lg:col-span-3">
          <div className="bg-[hsl(var(--card))] rounded-2xl border border-[hsl(var(--border))] shadow-sm overflow-hidden">
            <div className="p-6 sm:p-8">
              {activeTab === 'notifications' && (
                <div className="space-y-10">
                  {/* Info Box */}
                  <div className="bg-[#ebfbff] dark:bg-[#0f2744]/80 border-l-4 border-[#0ec2ec] dark:border-[#0088cc] p-6 flex gap-4 animate-in fade-in slide-in-from-top-2 duration-500 rounded-r-xl">
                    <div className="text-[#0ec2ec] dark:text-[#5ec8f0] pt-0.5">
                      <MdInfo className="w-6 h-6" />
                    </div>
                    <p className="text-[#0088cc] dark:text-[#b8dcf0] text-[15px] font-medium leading-relaxed">
                      All Euroasiann Marine Spares <span className="font-bold text-[#006699] dark:text-white">Notifications</span> and <span className="font-bold text-[#006699] dark:text-white">Chat Messages</span> will be sent by default to your registered email address below.
                    </p>
                  </div>

                  {/* Registered Email Address */}
                  <div className="bg-[hsl(var(--card))] rounded-xl border border-[hsl(var(--border))] overflow-hidden shadow-sm">
                    <div className="p-5 border-b border-[hsl(var(--border))]">
                      <h2 className="text-xl font-bold text-[hsl(var(--foreground))]">Registered Email Address</h2>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-left">
                        <thead>
                          <tr className="bg-[#e8f4fb] dark:bg-[#0f2744] border-b border-[#0088cc]/20">
                            <th className="px-6 py-4 text-[13px] font-bold text-[#0088cc] uppercase tracking-wider">
                              Email Account
                            </th>
                            <th className="px-6 py-4 text-[13px] font-bold text-[#0088cc] uppercase tracking-wider">
                              Selected Options
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-[hsl(var(--border))]">
                          <tr className="hover:bg-[#0088cc]/5 transition-colors">
                            <td className="px-6 py-6 text-[15px] font-medium text-[hsl(var(--foreground))]">
                              {user?.email || '—'}
                            </td>
                            <td className="px-6 py-6">
                              <div className="flex items-center gap-2 text-[14px] text-[hsl(var(--muted-foreground))]">
                                <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--muted-foreground))]" />
                                Receive all main emails
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* General Settings Section */}
                  <div className="bg-white rounded-xl border border-[#0088cc]/25/30 overflow-hidden shadow-sm">
                    <div className="p-5 border-b border-[#f0f0f0]">
                      <h2 className="text-xl font-bold text-[hsl(var(--foreground))]">General Settings</h2>
                    </div>

                    <div className="divide-y divide-[#f0f0f0]">
                      {/* Font Size Row */}
                      <div className="p-6 space-y-4">
                        <h3 className="inline-flex w-fit items-center rounded-lg bg-[#e8f4fb] px-3 py-1.5 text-[13px] font-bold uppercase tracking-wider text-[#0088cc] dark:bg-[#0f2744]">
                          Set Font Size
                        </h3>
                        <div className="flex flex-wrap gap-8">
                          {['Default (16px)', 'Medium (14px)', 'Small (13px)', 'Tiny (12px)'].map((size) => (
                            <label key={size} className="flex items-center gap-3 cursor-pointer group">
                              <input
                                type="radio"
                                name="fontSize"
                                checked={fontSize === size}
                                onChange={() => setFontSize(size as typeof fontSize)}
                                className="w-5 h-5 text-[#0088cc] border-[#d0d0d0] focus:ring-[#0088cc]/20"
                              />
                              <span className={`text-[15px] ${fontSize === size ? 'text-[hsl(var(--foreground))] font-semibold' : 'text-[#666666]'}`}>
                                {size}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>

                      {/* Desktop Notifications Row */}
                      <div className="p-6 space-y-6">
                        <div className="flex items-center gap-2">
                          <h3 className="inline-flex w-fit items-center rounded-lg bg-[#e8f4fb] px-3 py-1.5 text-[13px] font-bold uppercase tracking-wider text-[#0088cc] dark:bg-[#0f2744]">
                            Enable desktop notifications for
                          </h3>
                          <MdInfo className="w-4 h-4 text-[#0088cc]/60" />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-y-4 gap-x-6">
                          {[
                            { key: 'newPurchaseOrder', label: 'New Purchase Order' },
                            { key: 'newRFQ', label: 'New RFQ' },
                            { key: 'withdrawnOrder', label: 'Withdrawn Order' },
                            { key: 'rfqCancelled', label: 'RFQ Cancelled' },
                            { key: 'newChatMessage', label: 'New Chat Message' },
                            { key: 'reminderReceived', label: 'Reminder Received' },
                            { key: 'freightForwardingQuoteRequestSend', label: 'FREIGHT_FORWARDING_QUOTE_REQUEST_SEND' },
                            { key: 'freightForwardingAssignForwarder', label: 'FREIGHT_FORWARDING_ASSIGN_FORWARDER' },
                          ].map((item) => (
                            <label key={item.key} className="flex items-center gap-3 cursor-pointer group">
                              <input
                                type="checkbox"
                                checked={(desktopNotifications as any)[item.key]}
                                onChange={() => handleDesktopNotificationChange(item.key)}
                                className="w-5 h-5 rounded border-[#d0d0d0] text-[#0088cc] focus:ring-[#0088cc]/20"
                              />
                              <span className="text-[14px] text-[hsl(var(--foreground))] font-medium leading-tight break-words flex-1 min-w-0">
                                {item.label.includes('_') || item.label === item.label.toUpperCase()
                                  ? item.label.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')
                                  : item.label}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>

                      {/* Currency Row */}
                      <div className="p-6">
                        <div className="flex items-center gap-4">
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={defaultCurrency}
                              onChange={(e) => handleDefaultCurrencyChange(e.target.checked)}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-[#0088cc]/25 dark:bg-[#0088cc]/30 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-[#0088cc]/25 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#0088cc]"></div>
                          </label>
                          <div className="flex items-center gap-2">
                            <span className="text-[15px] font-semibold text-[hsl(var(--foreground))]">Set default currency</span>
                            <MdInfo className="w-4 h-4 text-[#cccccc]" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-[#f0f0f0] pt-6">
                    <h2 className="text-lg font-bold text-[hsl(var(--foreground))] mb-3">Traditional Notification Preferences</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {[
                        { key: 'emailNotifications' as const, label: 'Email Notifications', desc: 'Receive notifications via email' },
                        { key: 'smsNotifications' as const, label: 'SMS Notifications', desc: 'Receive notifications via SMS' },
                      ].map((item) => (
                        <div key={item.key} className="flex items-center justify-between gap-3 p-3 bg-[hsl(var(--background))] border border-[#d8d4b5]/30 rounded-lg">
                          <div className="min-w-0">
                            <h3 className="font-medium text-[hsl(var(--foreground))]">{item.label}</h3>
                            <p className="text-sm text-[hsl(var(--muted-foreground))]">{item.desc}</p>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer shrink-0">
                            <input
                              type="checkbox"
                              checked={notifications[item.key]}
                              onChange={() => handleNotificationChange(item.key)}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-[#0088cc]/25 dark:bg-[#0088cc]/30 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-[#0088cc]/25 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#0088cc] dark:peer-checked:bg-[#0088cc]" />
                          </label>
                        </div>
                      ))}
                    </div>

                    <div className="border-t border-[hsl(var(--border))] mt-4 pt-4">
                      <h3 className="font-semibold text-[hsl(var(--foreground))] mb-3">Alert Types</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {[
                          { key: 'rfqAlerts' as const, label: 'RFQ Alerts', desc: 'New RFQ requests' },
                          { key: 'orderAlerts' as const, label: 'Order Alerts', desc: 'Order updates' },
                          { key: 'paymentAlerts' as const, label: 'Payment Alerts', desc: 'Payment notifications' },
                          { key: 'shipmentAlerts' as const, label: 'Shipment Alerts', desc: 'Shipment status updates' },
                        ].map((item) => (
                          <div key={item.key} className="flex items-center justify-between gap-3 p-3 bg-[hsl(var(--background))] border border-[#d8d4b5]/30 rounded-lg">
                            <div className="min-w-0">
                              <h4 className="font-medium text-[hsl(var(--foreground))]">{item.label}</h4>
                              <p className="text-xs text-[hsl(var(--muted-foreground))]">{item.desc}</p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer shrink-0">
                              <input
                                type="checkbox"
                                checked={notifications[item.key]}
                                onChange={() => handleNotificationChange(item.key)}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-[#0088cc]/25 dark:bg-[#0088cc]/30 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-[#0088cc]/25 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#0088cc] dark:peer-checked:bg-[#0088cc]" />
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="border-t border-[hsl(var(--border))] mt-4 pt-4">
                      <h3 className="font-semibold text-[hsl(var(--foreground))] mb-3">Reports</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {[
                          { key: 'weeklyReports' as const, label: 'Weekly Reports', desc: 'Weekly summary reports' },
                          { key: 'monthlyReports' as const, label: 'Monthly Reports', desc: 'Monthly summary reports' },
                        ].map((item) => (
                          <div key={item.key} className="flex items-center justify-between gap-3 p-3 bg-[hsl(var(--background))] border border-[#d8d4b5]/30 rounded-lg">
                            <div className="min-w-0">
                              <h4 className="font-medium text-[hsl(var(--foreground))]">{item.label}</h4>
                              <p className="text-xs text-[hsl(var(--muted-foreground))]">{item.desc}</p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer shrink-0">
                              <input
                                type="checkbox"
                                checked={notifications[item.key]}
                                onChange={() => handleNotificationChange(item.key)}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-[#0088cc]/25 dark:bg-[#0088cc]/30 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-[#0088cc]/25 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#0088cc] dark:peer-checked:bg-[#0088cc]" />
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'display' && (
                <div className="space-y-6">
                  <div className="border-b border-[#0088cc]/20 pb-4">
                    <h2 className="text-xl font-bold text-[hsl(var(--foreground))]">{t('Display Preferences')}</h2>
                    <p className="text-sm text-[hsl(var(--foreground))]/60 mt-1 font-medium">
                      {t('Customize how information is displayed in your portal')}
                    </p>
                  </div>

                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">{t('Theme')}</label>
                      <select
                        value={display.theme}
                        onChange={(e) => handleDisplayChange('theme', e.target.value)}
                        className="w-full px-4 py-2.5 rounded-lg border border-[#d8d4b5] bg-[hsl(var(--background))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-[#b5bdd8]"
                      >
                        <option value="light">{t('Light')}</option>
                        <option value="dark">{t('Dark')}</option>
                        <option value="auto">{t('Auto (System)')}</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">{t('Language')}</label>
                      <select
                        value={display.language}
                        onChange={(e) => handleDisplayChange('language', e.target.value)}
                        className="w-full px-4 py-2.5 rounded-lg border border-[#d8d4b5] bg-[hsl(var(--background))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-[#b5bdd8]"
                      >
                        <option value="en">{t('English')}</option>
                        <option value="es">{t('Spanish')}</option>
                        <option value="fr">{t('French')}</option>
                        <option value="de">{t('German')}</option>
                        <option value="zh">{t('Chinese')}</option>
                      </select>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">{t('Date Format')}</label>
                        <select
                          value={display.dateFormat}
                          onChange={(e) => handleDisplayChange('dateFormat', e.target.value)}
                          className="w-full px-4 py-2.5 rounded-lg border border-[#d8d4b5] bg-[hsl(var(--background))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-[#b5bdd8]"
                        >
                          {DATE_FORMAT_OPTIONS.map((format) => (
                            <option key={format} value={format}>
                              {format}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">{t('Time Format')}</label>
                        <select
                          value={display.timeFormat}
                          onChange={(e) => handleDisplayChange('timeFormat', e.target.value)}
                          className="w-full px-4 py-2.5 rounded-lg border border-[#d8d4b5] bg-[hsl(var(--background))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-[#b5bdd8]"
                        >
                          <option value="12h">{t('12 Hour (AM/PM)')}</option>
                          <option value="24h">{t('24 Hour')}</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">{t('Currency')}</label>
                        <select
                          value={display.currency}
                          onChange={(e) => handleDisplayChange('currency', e.target.value)}
                          className="w-full px-4 py-2.5 rounded-lg border border-[#d8d4b5] bg-[hsl(var(--background))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-[#b5bdd8]"
                        >
                          {ALL_CURRENCIES.map((opt) => (
                            <option key={opt.code} value={opt.code}>
                              {opt.code} ({opt.symbol}) — {opt.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">Items Per Page</label>
                        <select
                          value={display.itemsPerPage}
                          onChange={(e) => handleDisplayChange('itemsPerPage', parseInt(e.target.value))}
                          className="w-full px-4 py-2.5 rounded-lg border border-[#d8d4b5] bg-[hsl(var(--background))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-[#b5bdd8]"
                        >
                          <option value="10">10</option>
                          <option value="20">20</option>
                          <option value="50">50</option>
                          <option value="100">100</option>
                        </select>
                      </div>

                      <div className="flex items-center justify-between p-4 bg-[hsl(var(--background))] border border-[#d8d4b5]/30 rounded-lg col-span-2">
                        <div>
                          <h3 className="font-medium text-[hsl(var(--foreground))]">{t('Enable Shipment Map')}</h3>
                          <p className="text-sm text-[hsl(var(--foreground))]/60">{t('Display the interactive tracking map on shipment detail pages')}</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={display.shipmentMapEnabled}
                            onChange={(e) => handleDisplayChange('shipmentMapEnabled', e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-[#0088cc]/25 dark:bg-[#0088cc]/30 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-[#0088cc]/25 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#0088cc] dark:peer-checked:bg-[#0088cc]" />
                        </label>
                      </div>
                    </div>

                    <div className="flex justify-end pt-4 border-t border-[hsl(var(--border))]">
                      <button
                        type="button"
                        onClick={handleApplyDisplaySettings}
                        disabled={!hasDisplayChanges}
                        className="px-6 py-2.5 rounded-lg bg-[#0088cc] text-white font-semibold text-sm hover:bg-[#0077b3] transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#0088cc]"
                      >
                        {t('Apply Display Preferences')}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'security' && (
                <div className="space-y-6">
                  <div className="border-b border-[#0088cc]/20 pb-4">
                    <h2 className="text-xl font-bold text-[hsl(var(--foreground))]">Security Settings</h2>
                    <p className="text-sm text-[hsl(var(--foreground))]/60 mt-1 font-medium">
                      Manage your account security and privacy settings
                    </p>
                  </div>

                  <div className="space-y-6 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5">
                    <div className="flex items-center justify-between p-4 bg-[hsl(var(--background))] border border-[#d8d4b5]/30 rounded-lg">
                      <div>
                        <h3 className="font-medium text-[hsl(var(--foreground))]">Two-Factor Authentication</h3>
                        <p className="text-sm text-[hsl(var(--foreground))]/60">Add an extra layer of security to your account</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={security.twoFactorEnabled}
                          onChange={(e) => handleSecurityChange('twoFactorEnabled', e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-[#0088cc]/25 dark:bg-[#0088cc]/30 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-[#0088cc]/25 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#0088cc] dark:peer-checked:bg-[#0088cc]" />
                      </label>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">Session Timeout (minutes)</label>
                      <input
                        type="number"
                        min="5"
                        max="480"
                        value={security.sessionTimeout}
                        onChange={(e) => handleSecurityChange('sessionTimeout', parseInt(e.target.value))}
                        className="w-full px-4 py-2.5 rounded-lg border border-[#d8d4b5] bg-[hsl(var(--background))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-[#b5bdd8]"
                      />
                      <p className="text-xs text-[hsl(var(--foreground))]/60 mt-1">Automatically log out after inactivity</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">Password Expiry (days)</label>
                      <input
                        type="number"
                        min="30"
                        max="365"
                        value={security.passwordExpiry}
                        onChange={(e) => handleSecurityChange('passwordExpiry', parseInt(e.target.value))}
                        className="w-full px-4 py-2.5 rounded-lg border border-[#d8d4b5] bg-[hsl(var(--background))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-[#b5bdd8]"
                      />
                      <p className="text-xs text-[hsl(var(--foreground))]/60 mt-1">Require password change after this many days</p>
                    </div>

                    <div className="flex justify-end pt-2">
                      <button
                        type="button"
                        onClick={handleSaveSecuritySettings}
                        disabled={!hasSecurityChanges || securitySaving}
                        className="inline-flex items-center justify-center rounded-lg bg-[#0088cc] px-6 py-2.5 text-sm font-bold text-white shadow-sm transition-colors hover:bg-[#006699] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-[#0088cc]"
                      >
                        {securitySaving ? 'Saving...' : 'Save changes'}
                      </button>
                    </div>
                  </div>

                  <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5">
                    <button
                      type="button"
                      onClick={() => setPasswordSectionOpen((open) => !open)}
                      className="flex w-full items-center justify-between gap-3 text-left"
                      aria-expanded={passwordSectionOpen}
                    >
                      <h3 className="font-semibold text-[hsl(var(--foreground))]">Change Password</h3>
                      <MdKeyboardArrowDown
                        className={`h-6 w-6 shrink-0 text-[#0088cc] transition-transform ${passwordSectionOpen ? 'rotate-180' : ''}`}
                      />
                    </button>
                    {passwordSectionOpen && (
                      <div className="mt-4 space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">Current Password</label>
                          <div className="relative">
                            <input
                              type={showPassword ? 'text' : 'password'}
                              value={passwordForm.currentPassword}
                              onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                              className="w-full px-4 py-2.5 rounded-lg border border-[#d8d4b5] bg-[hsl(var(--background))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-[#b5bdd8]"
                              placeholder="Enter current password"
                            />
                            <button
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(var(--foreground))]/60 hover:text-[hsl(var(--foreground))]"
                            >
                              {showPassword ? <MdVisibilityOff className="w-5 h-5" /> : <MdVisibility className="w-5 h-5" />}
                            </button>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">New Password</label>
                          <div className="relative">
                            <input
                              type={showNewPassword ? 'text' : 'password'}
                              value={passwordForm.newPassword}
                              onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                              className="w-full px-4 py-2.5 pr-10 rounded-lg border border-[#d8d4b5] bg-[hsl(var(--background))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-[#b5bdd8]"
                              placeholder="Min 8 chars, uppercase, number, symbol"
                            />
                            <button
                              type="button"
                              onClick={() => setShowNewPassword((open) => !open)}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(var(--foreground))]/60 hover:text-[hsl(var(--foreground))]"
                              aria-label={showNewPassword ? 'Hide new password' : 'Show new password'}
                            >
                              {showNewPassword ? <MdVisibilityOff className="w-5 h-5" /> : <MdVisibility className="w-5 h-5" />}
                            </button>
                          </div>
                          <PasswordRequirementsHint password={passwordForm.newPassword} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">Confirm New Password</label>
                          <div className="relative">
                            <input
                              type={showConfirmPassword ? 'text' : 'password'}
                              value={passwordForm.confirmPassword}
                              onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                              className="w-full px-4 py-2.5 pr-10 rounded-lg border border-[#d8d4b5] bg-[hsl(var(--background))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-[#b5bdd8]"
                              placeholder="Confirm new password"
                            />
                            <button
                              type="button"
                              onClick={() => setShowConfirmPassword((open) => !open)}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(var(--foreground))]/60 hover:text-[hsl(var(--foreground))]"
                              aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                            >
                              {showConfirmPassword ? <MdVisibilityOff className="w-5 h-5" /> : <MdVisibility className="w-5 h-5" />}
                            </button>
                          </div>
                        </div>
                        <div className="flex justify-end pt-1">
                          <button
                            onClick={handleChangePassword}
                            disabled={
                              changePasswordMutation.isPending ||
                              !passwordForm.currentPassword ||
                              !isPasswordStrong(passwordForm.newPassword) ||
                              passwordForm.newPassword !== passwordForm.confirmPassword
                            }
                            className="px-4 py-2 rounded-lg bg-[#0088cc] text-white hover:bg-[#006699] transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {changePasswordMutation.isPending ? 'Updating...' : 'Update Password'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'procurement' && (
                <div className="space-y-6">
                  <div className="border-b border-[#0088cc]/20 pb-4">
                    <h2 className="text-xl font-bold text-[hsl(var(--foreground))]">Procurement Settings</h2>
                    <p className="text-sm text-[hsl(var(--foreground))]/60 mt-1 font-medium">
                      Manage your procurement and negotiation workflows
                    </p>
                  </div>

                  <div className="space-y-6">
                    <div className="flex items-center justify-between p-4 bg-[hsl(var(--background))] border border-[#d8d4b5]/30 rounded-lg">
                      <div>
                        <h3 className="font-medium text-[hsl(var(--foreground))]">Negotiation Loop</h3>
                        <p className="text-sm text-[hsl(var(--foreground))]/60">Enable automated negotiation logic for quotations</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={procurement.negotiationLoopEnabled}
                          onChange={(e) => handleProcurementChange('negotiationLoopEnabled', e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-[#0088cc]/25 dark:bg-[#0088cc]/30 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-[#0088cc]/25 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#0088cc] dark:peer-checked:bg-[#0088cc]" />
                      </label>
                    </div>
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function PaymentSettingsContent() {
  const { showToast } = useToast();
  const { currency: portalCurrency } = useCurrency();
  const [bankDetails, setBankDetails] = useState({
    accountName: '',
    accountNumber: '',
    bankName: '',
    branchName: '',
    swiftCode: '',
    iban: '',
    currency: portalCurrency,
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setIsLoading(true);
    try {
      const response = await authenticatedFetch('/api/v1/admin/settings/payment-settings');
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data?.data) {
          setBankDetails((prev) => ({ ...prev, ...result.data.data }));
        }
      }
    } catch (error) {
      console.error('Failed to fetch payment settings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const response = await authenticatedFetch('/api/v1/admin/settings/payment-settings', {
        method: 'PUT',
        body: JSON.stringify({
          data: bankDetails,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to save payment settings');
      }

      showToast('Banking details saved successfully', 'success');
    } catch (error: any) {
      showToast(error.message, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) return <PageSkeleton variant="panel" />;

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-[hsl(var(--foreground))]">Global Banking Details</h2>
      <p className="text-sm text-[hsl(var(--muted-foreground))]">
        These details will be shown to customers during the onboarding process for manual bank transfers.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">Account Name</label>
          <input
            type="text"
            value={bankDetails.accountName}
            onChange={(e) => setBankDetails({ ...bankDetails, accountName: e.target.value })}
            className="w-full px-4 py-2 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
            placeholder="Enterprise Solutions Ltd"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">Account Number</label>
          <input
            type="text"
            value={bankDetails.accountNumber}
            onChange={(e) => setBankDetails({ ...bankDetails, accountNumber: e.target.value })}
            className="w-full px-4 py-2 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
            placeholder="1234567890"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">Bank Name</label>
          <input
            type="text"
            value={bankDetails.bankName}
            onChange={(e) => setBankDetails({ ...bankDetails, bankName: e.target.value })}
            className="w-full px-4 py-2 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
            placeholder="Global Business Bank"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">Branch Name</label>
          <input
            type="text"
            value={bankDetails.branchName}
            onChange={(e) => setBankDetails({ ...bankDetails, branchName: e.target.value })}
            className="w-full px-4 py-2 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
            placeholder="Main Financial District"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">SWIFT / BIC Code</label>
          <input
            type="text"
            value={bankDetails.swiftCode}
            onChange={(e) => setBankDetails({ ...bankDetails, swiftCode: e.target.value })}
            className="w-full px-4 py-2 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
            placeholder="GBBKUS33"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">IBAN</label>
          <input
            type="text"
            value={bankDetails.iban}
            onChange={(e) => setBankDetails({ ...bankDetails, iban: e.target.value })}
            className="w-full px-4 py-2 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
            placeholder="US12 3456 7890 1234 5678 90"
          />
        </div>
      </div>

      <div className="pt-4">
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="px-6 py-2 rounded-lg bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          {isSaving ? 'Saving...' : 'Save Banking Details'}
        </button>
      </div>
    </div>
  );
}


